import React, { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Header } from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Sparkles, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { EXPERIENCES_CONFIG } from './experiencesConfig';
import { useCmsSection } from '../../hooks/useCmsContent';

// Resolve relative /api/uploads/... paths to the full preview URL.
const _API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const cmsUrl = (u) => {
  if (!u) return '';
  if (/^https?:\/\//.test(u)) return u;
  if (u.startsWith('/api/')) return `${_API_BASE}${u}`;
  return u;
};

/**
 * Drives every Unique Experience detail page.
 * Route: /unique-experiences/:slug
 * Config: see experiencesConfig.js — the image URLs + copy live there.
 *
 * Admins can override title / subtitle / intro / hero image / extra sections
 * via /crm/settings/landing → "Experience Detail Pages". CMS data layered on
 * top of the static config so a partial admin override (e.g. just intro)
 * keeps everything else intact.
 */
export const ExperienceDetailTemplate = ({ config: configProp }) => {
  const params = useParams();
  const baseConfig = configProp || EXPERIENCES_CONFIG[params.slug];
  const cmsAllPages = useCmsSection('experience_pages', {});
  const cmsPage = (cmsAllPages && params.slug) ? cmsAllPages[params.slug] : null;

  const [active, setActive] = useState(0);

  if (!baseConfig) {
    return <Navigate to="/" replace />;
  }

  // Merge: CMS overrides win over static config when set.
  const config = {
    ...baseConfig,
    title:        (cmsPage && cmsPage.title)    || baseConfig.title,
    subtitle:     (cmsPage && cmsPage.subtitle) || baseConfig.subtitle,
    heroTagline:  (cmsPage && cmsPage.intro)    || baseConfig.heroTagline,
    heroImage:    (cmsPage && cmsPage.hero_image)
      ? { src: cmsUrl(cmsPage.hero_image), alt: (cmsPage.title || baseConfig.title) }
      : baseConfig.heroImage,
    // "What to Expect" steps — when the admin saves a non-empty array via the
    // Landing Editor, we use those instead of the bundled defaults. Emoji icons
    // saved from the admin override the lucide-react icon component.
    steps: (cmsPage && Array.isArray(cmsPage.steps) && cmsPage.steps.length > 0)
      ? cmsPage.steps
      : baseConfig.steps,
    stepsHeading: (cmsPage && cmsPage.steps_heading) || baseConfig.stepsHeading,
    stepsIntro:   (cmsPage && cmsPage.steps_intro)   || baseConfig.stepsIntro,
    // "See it in action" gallery — admin-uploaded images take precedence.
    galleryImages: (cmsPage && Array.isArray(cmsPage.gallery_images) && cmsPage.gallery_images.length > 0)
      ? cmsPage.gallery_images.map((g) => ({
          ...g,
          src: cmsUrl(g.src || g.url || ''),
        }))
      : baseConfig.galleryImages,
    galleryHeading: (cmsPage && cmsPage.gallery_heading) || baseConfig.galleryHeading,
  };
  const cmsSections = (cmsPage && Array.isArray(cmsPage.sections)) ? cmsPage.sections : [];

  const Icon = config.icon || Sparkles;
  const brand = config.brandColor || '#FF3B6B';
  const accentBg = config.accentBg || '#FFE8EC';
  const gallery = config.galleryImages || [];

  return (
    <div className="min-h-screen bg-[#FFFDF8]">
      <Header />

      {/* HERO */}
      <section
        className="relative overflow-hidden"
        style={{ background: config.heroGradient || 'linear-gradient(135deg, #FFE8EC 0%, #FFF4E6 100%)' }}
        data-testid="experience-hero"
      >
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <div className="absolute top-10 left-[10%] w-40 h-40 rounded-full blur-3xl" style={{ background: brand }} />
          <div className="absolute bottom-10 right-[8%] w-56 h-56 rounded-full blur-3xl" style={{ background: '#06D6A0' }} />
          <div className="absolute top-1/2 right-[30%] w-28 h-28 rounded-full blur-2xl" style={{ background: '#FFD166' }} />
        </div>

        <div className="relative container mx-auto px-4 py-10 sm:py-14">
          <Link to="/#packages" className="inline-flex items-center gap-1 text-sm text-[#6B6B7A] hover:text-[#1E1E24] transition-colors mb-4" data-testid="experience-back-link">
            <ArrowLeft className="w-4 h-4" /> Back to packages
          </Link>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-2xl bg-white flex items-center justify-center shadow-sm">
                <Icon className="w-5 h-5" style={{ color: brand }} />
              </div>
              <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-white/70 backdrop-blur-sm uppercase tracking-wide" style={{ color: brand }}>
                {config.subtitle || 'Unique Experience'}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#1E1E24] mb-3 leading-tight" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {config.title}
            </h1>
            <p className="text-base sm:text-lg text-[#4A4A55] max-w-2xl leading-relaxed">
              {config.heroTagline}
            </p>

            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/book" data-testid="experience-hero-cta">
                <Button className="rounded-full text-white px-5 py-4 text-sm shadow-sm hover:opacity-90" style={{ backgroundColor: brand }}>
                  Add this to our party <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="rounded-full border-white bg-white/70 hover:bg-white backdrop-blur-sm px-5 py-4 text-sm">
                  Ask us a question
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHAT TO EXPECT */}
      {config.steps?.length > 0 && (
        <section className="py-10 sm:py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mb-6">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: accentBg, color: brand }}>
                WHAT TO EXPECT
              </span>
              <h2 className="text-2xl sm:text-3xl font-semibold mt-3 mb-2 text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                {config.stepsHeading || 'Four steps to an unforgettable station'}
              </h2>
              <p className="text-sm text-[#6B6B7A]">
                {config.stepsIntro || "From the moment we arrive to the final photo, here's how the station flows."}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="experience-steps">
              {config.steps.map((s, i) => {
                // Admin-saved steps store the icon as an emoji string; the
                // bundled defaults use lucide-react components. Detect which
                // and render appropriately so both keep working.
                const isEmojiIcon = typeof s.icon === 'string';
                const StepIcon = isEmojiIcon ? null : (s.icon || Sparkles);
                return (
                  <motion.div
                    key={`${s.title || 'step'}-${i}`}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.08 }}
                    viewport={{ once: true }}
                    className="bg-[#FFFDF8] rounded-2xl border border-[#E8E5DF] p-4 hover:shadow-md transition-all duration-200"
                    style={{ '--hoverBorder': brand }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = brand; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E8E5DF'; }}
                    data-testid={`experience-step-${i}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-2xl font-semibold" style={{ fontFamily: 'Fredoka, sans-serif', color: brand }}>
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <div className="w-8 h-8 rounded-xl flex items-center justify-center text-base" style={{ backgroundColor: accentBg }}>
                        {isEmojiIcon ? (
                          <span aria-hidden>{s.icon}</span>
                        ) : (
                          <StepIcon className="w-4 h-4" style={{ color: brand }} />
                        )}
                      </div>
                    </div>
                    <h3 className="font-semibold text-[#1E1E24] mb-1 text-sm" style={{ fontFamily: 'Fredoka, sans-serif' }}>{s.title}</h3>
                    <p className="text-xs text-[#6B6B7A] leading-relaxed">{s.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CMS-MANAGED EXTRA SECTIONS — admins can add detail blocks via
          /crm/settings/landing → Experience Detail Pages */}
      {cmsSections.length > 0 && (
        <section className="py-8 bg-white" data-testid="experience-cms-sections">
          <div className="container mx-auto px-4 max-w-4xl space-y-8">
            {cmsSections.map((s, i) => (
              <div key={`${s.heading || 'section'}-${i}`} className="grid grid-cols-1 md:grid-cols-2 gap-5 items-center">
                {s.image_url && (
                  <img
                    src={cmsUrl(s.image_url)}
                    alt={s.heading || `Section ${i + 1}`}
                    className={`w-full max-h-[200px] object-cover rounded-2xl shadow-sm ${i % 2 === 1 ? 'md:order-2' : ''}`}
                    loading="lazy"
                  />
                )}
                <div className={s.image_url ? '' : 'md:col-span-2'}>
                  {s.heading && (
                    <h3 className="text-xl sm:text-2xl font-semibold text-[#1E1E24] mb-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                      {s.heading}
                    </h3>
                  )}
                  {s.body && (
                    <div className="text-sm text-[#4A4A55] leading-relaxed whitespace-pre-line">{s.body}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* EXPERIENCE */}
      <section className="py-10 sm:py-12" style={{ backgroundColor: '#FFF4E6' }}>
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-center">
            <div className="lg:col-span-3">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-white text-[#9C6B12]">THE EXPERIENCE</span>
              <h2 className="text-2xl sm:text-3xl font-semibold mt-3 mb-3 text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                {config.experienceHeading}
              </h2>
              {(config.experienceCopy || []).map((p, i) => (
                <p key={`copy-${i}-${p.slice(0, 24)}`} className="text-sm text-[#4A4A55] leading-relaxed mb-3">{p}</p>
              ))}

              {config.bulletPoints?.length > 0 && (
                <ul className="space-y-1.5 mt-2" data-testid="experience-bullets">
                  {config.bulletPoints.map((b) => (
                    <li key={`bullet-${b}`} className="flex items-start gap-2 text-sm text-[#1E1E24]">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[#06D6A0]/20 flex items-center justify-center mt-0.5">
                        <Check className="w-3 h-3 text-[#058B69]" />
                      </span>
                      {b}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="lg:col-span-2">
              {config.heroImage?.src && (
                <div className="rounded-3xl overflow-hidden shadow-lg relative border-4 border-white">
                  <img
                    src={config.heroImage.src}
                    alt={config.heroImage.alt || config.title}
                    className="w-full max-h-[300px] object-cover"
                    loading="lazy"
                  />
                  {config.realMomentCaption && (
                    <div className="absolute bottom-3 left-3 right-3 bg-white/95 backdrop-blur-sm rounded-2xl p-2">
                      <p className="text-[10px] text-[#6B6B7A] uppercase tracking-wide">Real party moment</p>
                      <p className="text-xs text-[#1E1E24] font-medium">{config.realMomentCaption}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* GALLERY */}
      {gallery.length > 0 && (
        <section className="py-10 sm:py-12 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mb-6">
              <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-[#F0F0FF] text-[#6366F1]">GALLERY</span>
              <h2 className="text-2xl sm:text-3xl font-semibold mt-3 text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                {config.galleryHeading || 'See it in action'}
              </h2>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="experience-gallery">
              {gallery.map((g, i) => (
                <motion.figure
                  key={g.src || `gallery-${i}`}
                  onMouseEnter={() => setActive(i)}
                  whileHover={{ y: -3 }}
                  transition={{ duration: 0.2 }}
                  className={`relative rounded-2xl overflow-hidden group cursor-pointer h-[200px] shadow-sm border-2 transition-colors ${active === i ? '' : 'border-transparent'}`}
                  style={active === i ? { borderColor: brand } : {}}
                  data-testid={`experience-gallery-img-${i}`}
                >
                  <img src={g.src} alt={g.alt || `${config.title} photo ${i + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  {g.caption && (
                    <figcaption className="absolute bottom-0 left-0 right-0 p-3 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <p className="text-xs font-medium">{g.caption}</p>
                    </figcaption>
                  )}
                </motion.figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-10 sm:py-12" style={{ background: config.ctaGradient || 'linear-gradient(135deg, #FF3B6B 0%, #FF7A9C 100%)' }}>
        <div className="container mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-2xl sm:text-4xl font-semibold text-white mb-3" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {config.ctaHeading || `Ready for ${config.title} at your party?`}
            </h2>
            <p className="text-white/90 max-w-2xl mx-auto mb-5 text-sm sm:text-base">
              {config.ctaCopy || `Add the ${config.title} station to any Creative Birthdays package — or build a custom party around it.`}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/book" data-testid="experience-cta-book">
                <Button className="rounded-full bg-white hover:bg-[#FFFDF8] px-6 py-4 text-sm font-semibold shadow-lg" style={{ color: brand }}>
                  Book a party <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
              <Link to="/#packages" data-testid="experience-cta-packages">
                <Button variant="outline" className="rounded-full border-white text-white hover:bg-white px-6 py-4 text-sm font-semibold bg-transparent" style={{ '--hoverText': brand }} onMouseEnter={(e) => { e.currentTarget.style.color = brand; }} onMouseLeave={(e) => { e.currentTarget.style.color = '#fff'; }}>
                  See all packages
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ExperienceDetailTemplate;
